import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Card, Table, Button, Alert, Badge, Spinner, Row, Col, Modal, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  useGetCrmSyncQuery, useRunCrmSyncMutation, useRunCrmMatchMutation, useUploadCrmExportMutation,
  useGetCrmSyncOptionsQuery, useSaveCrmSourceMutation,
} from '../app/api';

// Super Admin -> CRM Sync.
//
// Refreshes a merging firm's client book from its Redtail database: re-pulls
// contacts and accounts into staging and rebuilds what Work -> CRM shows. One
// card per firm, because each firm's Redtail is its own database and refreshes
// on its own. Then one "Match to Salesforce" run ties every firm's clients to
// EXISTING FSC households. Manual only for now - nothing runs on a schedule.
// Firms are added and edited here too ("Add firm"); there is no delete, since
// removing a firm would take its clients with it.

const MATCH_LABEL = 'salesforce';

const STATUS = {
  queued:  { bg: 'secondary', label: 'Queued' },
  running: { bg: 'info',      label: 'Running' },
  success: { bg: 'success',   label: 'Success' },
  error:   { bg: 'danger',    label: 'Failed' },
};

function when(ts) {
  if (!ts) return '-';
  const d = new Date(String(ts).endsWith('Z') ? ts : `${ts}Z`);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

const n = (v) => (v == null ? '-' : Number(v).toLocaleString());

function duration(run) {
  if (!run?.started || !run?.finished) return null;
  const ms = new Date(`${run.finished}Z`) - new Date(`${run.started}Z`);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const m = Math.round(ms / 60000);
  return m < 1 ? 'under a minute' : `${m} min`;
}

// One line describing a finished run - shared by the card and the history.
function describe(run) {
  const d = run?.detail;
  if (typeof d !== 'object' || d === null) {
    return <span className={run?.status === 'error' ? 'text-danger' : 'text-muted'}>{d || run?.progress || '-'}</span>;
  }
  if (run.source_label === MATCH_LABEL) {
    return (
      <>
        <strong>{n(d.linked)}</strong> linked · {n(d.needs_review)} possible matches to review
        {' '}· {n(d.no_match)} not in Salesforce
        <span className="text-muted"> (of {n(d.contacts)} contacts, against {n(d.fsc_people)} Salesforce people)</span>
      </>
    );
  }
  if (run.mode === 'upload') {
    return (
      <>
        <strong>{n(d.clients_after_dedupe)}</strong> people after grouping {n(d.duplicate_records_grouped)} duplicates
        {d.policies != null && <> · <strong>{n(d.policies_attached)}</strong> of {n(d.policies)} policies attached</>}
        {d.policies_ambiguous > 0 && <span className="text-muted"> ({n(d.policies_ambiguous)} same-name, {n(d.policies_unmatched)} no matching contact)</span>}
      </>
    );
  }
  return (
    <>
      <strong>{n(d.contacts)}</strong> contacts{d.since ? ' changed' : ''},{' '}
      <strong>{n(d.accounts)}</strong> accounts
      {d.marked_deleted > 0 && <> · {n(d.marked_deleted)} gone from Redtail</>}
      {d.accounts_removed > 0 && <> · {n(d.accounts_removed)} accounts removed</>}
      {' '}· {n(d.clients_after_dedupe)} people after grouping {n(d.duplicate_records_grouped)} duplicates
    </>
  );
}

function daysOld(ts) {
  if (!ts) return null;
  const d = new Date(String(ts).endsWith('Z') ? ts : `${ts}Z`);
  return Number.isNaN(d.getTime()) ? null : Math.floor((Date.now() - d) / 86400000);
}

// "Match to Salesforce": one run across every firm. Its quality depends on two
// copies refreshed elsewhere, so the card says how old they are and links to
// the page that refreshes each.
function MatchCard({ match, sources, last, busy, onRun }) {
  const active = match?.active;
  const fscAge = daysOld(match?.fsc_synced_at);
  const orionAge = daysOld(match?.orion_synced_at);
  const stale = (age) => age === null || age > 7;
  const byFirm = match?.by_firm || {};
  return (
    <Card className={`mb-3 ${active ? 'border-info' : ''}`}>
      <Card.Body>
        <p className="text-muted mb-3" style={{ fontSize: '0.85rem', maxWidth: '48rem' }}>
          Ties each firm&apos;s clients to the household they <strong>already have</strong> in Salesforce, so
          Work - CRM can show the link and Orion AUM. It only reads Salesforce: no household is ever created
          or changed. An SSN or email match links straight away; a date of birth + ZIP or name + ZIP match is
          saved as a possible match and is not shown as linked until a person confirms it.
        </p>

        <div className="d-flex gap-4 flex-wrap mb-3" style={{ fontSize: '0.82rem' }}>
          {[['Salesforce households', match?.fsc_synced_at, fscAge, '/admin/fsc-sync', 'FSC Sync'],
            ['Orion AUM', match?.orion_synced_at, orionAge, '/admin/orion-sync', 'Orion Sync']].map(([label, at, age, path, page]) => (
            <div key={label}>
              <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label} copy refreshed</div>
              <span className={stale(age) ? 'text-warning fw-bold' : ''}>
                {stale(age) && <i className="bi bi-exclamation-triangle-fill me-1" />}
                {at ? `${when(at)} (${age} day${age === 1 ? '' : 's'} ago)` : 'never'}
              </span>
              {stale(age) && <div><Link to={path}>Refresh it on {page}</Link> first</div>}
            </div>
          ))}
        </div>

        {Object.keys(byFirm).length > 0 && (
          <Table size="sm" className="mb-3" style={{ fontSize: '0.82rem', maxWidth: '40rem' }}>
            <thead><tr><th>Firm</th><th className="text-end">Linked</th><th className="text-end">To review</th><th className="text-end">Not in Salesforce</th></tr></thead>
            <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
              {Object.entries(byFirm).map(([label, c]) => (
                <tr key={label}>
                  <td>{sources.find((x) => x.label === label)?.display_name || label}</td>
                  <td className="text-end fw-bold">{n(c.linked)}</td>
                  <td className="text-end">{n(c.needs_review)}</td>
                  <td className="text-end">{n(c.no_match)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {active ? (
          <Alert variant="info" className="d-flex align-items-center gap-2 mb-0">
            <Spinner animation="border" size="sm" />
            <div>
              <strong>Matching</strong>{' - '}{active.progress || 'starting...'}
              <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                Started by {active.started_by} at {when(active.started)}. Usually a minute or two.
              </div>
            </div>
          </Alert>
        ) : (
          <Button variant="primary" onClick={onRun} disabled={busy || sources.some((x) => x.active)}>
            {busy ? <Spinner animation="border" size="sm" /> : <i className="bi bi-link-45deg" />} Match to Salesforce
          </Button>
        )}
        {!active && sources.some((x) => x.active) && (
          <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>Waits until the firm refresh above finishes.</span>
        )}

        {last && !active && (
          <div className="mt-3 pt-2 border-top" style={{ fontSize: '0.8rem' }}>
            <Badge bg={(STATUS[last.status] || {}).bg || 'secondary'} className="me-2">
              {(STATUS[last.status] || {}).label || last.status}
            </Badge>
            <span className="text-muted">Last match {when(last.finished)} by {last.started_by}</span>
            <div className="mt-1">{describe(last)}</div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
MatchCard.propTypes = {
  match: PropTypes.object,
  sources: PropTypes.array.isRequired,
  last: PropTypes.object,
  busy: PropTypes.bool,
  onRun: PropTypes.func.isRequired,
};

function Stat({ label, value, note }) {
  return (
    <div>
      <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label}</div>
      <strong style={{ fontSize: '1.15rem' }}>{value}</strong>
      {note && <div className="text-muted" style={{ fontSize: '0.7rem' }}>{note}</div>}
    </div>
  );
}
Stat.propTypes = { label: PropTypes.string, value: PropTypes.node, note: PropTypes.node };

// SmartOffice firms have no API we can call, so their card takes the list
// exports instead, recognized by their column headings: contacts and policies,
// plus businesses and investment accounts where the firm exports them (JLS).
// Each upload replaces the previous one of that kind and queues a rebuild;
// exports the CRM does not read (positions, households...) are skipped.
const UPLOAD_KINDS = [
  ['contacts', 'Contact list', true],
  ['policies', 'Policy list', true],
  ['businesses', 'Business list', false],
  ['accounts', 'Investment accounts', false],
  ['tax_flags', 'Tax flags', false],
];

// The API refuses a file over 60 MB and nginx an upload over 200 MB (a bare
// 413 page). Checked here first, so the person is told which file and why.
const MAX_FILE_MB = 60;
const MAX_UPLOAD_MB = 200;
const mb = (bytes) => bytes / (1024 * 1024);

function sizeProblem(files) {
  const big = files.filter((f) => mb(f.size) > MAX_FILE_MB);
  if (big.length) {
    return `${big.map((f) => `${f.name} (${Math.round(mb(f.size))} MB)`).join(', ')} ${big.length > 1 ? 'are' : 'is'} over the ${MAX_FILE_MB} MB limit per file. `
      + 'SmartOffice\'s "MS Excel" export writes UTF-16, twice the size - export again as "CSV - Comma Separated Value - Unformatted", or re-save the file as UTF-8.';
  }
  const total = files.reduce((t, f) => t + f.size, 0);
  if (mb(total) > MAX_UPLOAD_MB) {
    return `These files add up to ${Math.round(mb(total))} MB, over the ${MAX_UPLOAD_MB} MB limit for one upload. Upload them in two groups `
      + '(all the pieces of one list must still go in the same group).';
  }
  return null;
}

function UploadPanel({ source, busy, onUpload }) {
  const [files, setFiles] = useState([]);
  const [inputKey, setInputKey] = useState(0);
  const [problem, setProblem] = useState(null);
  const [update, setUpdate] = useState(false);
  const uploads = source.uploads || {};
  const pick = (list) => { setFiles(list); setProblem(sizeProblem(list)); };
  const go = async () => {
    const tooBig = sizeProblem(files);
    if (tooBig) { setProblem(tooBig); return; }
    const result = await onUpload(source.label, files, update);
    if (result === true) {
      setFiles([]);
      setProblem(null);
      setUpdate(false);
      setInputKey((k) => k + 1);
    } else {
      setProblem(result);
    }
  };
  return (
    <>
      <div className="d-flex gap-4 flex-wrap mb-3" style={{ fontSize: '0.8rem' }}>
        {UPLOAD_KINDS.map(([kind, label, required]) => (
          <div key={kind}>
            <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label} on file</div>
            {uploads[kind]
              ? <span>{n(uploads[kind].n)} rows · {uploads[kind].filename} · {when(uploads[kind].imported_at)} by {uploads[kind].imported_by}</span>
              : required
                ? <span className="text-warning fw-bold">Not uploaded yet</span>
                : <span className="text-muted">None (optional)</span>}
          </div>
        ))}
      </div>
      <div className="d-flex gap-2 flex-wrap align-items-center">
        <input
          key={inputKey}
          type="file"
          accept=".csv,text/csv"
          multiple
          className="form-control form-control-sm"
          style={{ maxWidth: '26rem' }}
          aria-label="SmartOffice export files"
          onChange={(e) => pick(Array.from(e.target.files || []))}
        />
        <Button variant="primary" onClick={go} disabled={busy || files.length === 0 || Boolean(sizeProblem(files))}>
          {busy ? <Spinner animation="border" size="sm" /> : <i className="bi bi-upload" />}{' '}
          Upload and rebuild
        </Button>
        <Form.Check
          type="checkbox"
          id={`update-${source.label}`}
          checked={update}
          onChange={(e) => setUpdate(e.target.checked)}
          label="Add to what's on file (match by SmartOffice ID)"
          title="For a list too big for one upload: send it in pieces, one upload each. Rows with an ID already on file are updated, new ones added, nothing removed."
          style={{ fontSize: '0.8rem' }}
        />
      </div>
      {/* Here, not in the page-top alert: the card is usually scrolled far below it. */}
      {problem && <Alert variant="danger" className="mt-2 mb-0 py-2" style={{ fontSize: '0.82rem', maxWidth: '48rem' }}>{problem}</Alert>}
      <div className="text-muted mt-2" style={{ fontSize: '0.78rem', maxWidth: '48rem' }}>
        In SmartOffice, open the list (People &amp; Companies for contacts, Insurance - Policies for
        policies), then Menu - List Options - Export with <strong>CSV - Comma Separated Value -
        Unformatted</strong> and <strong>Include Column Headings</strong> ticked. Pick the files
        here; each upload replaces the previous one of the same list. A list exported in
        pieces (for example one Policy Type at a time) can be uploaded together - pick all the
        pieces at once and they are combined. A Business list and an Investment Account
        list are read too when the firm exports them, and a tax flags export shows each
        tax-client method on Work -&gt; CRM; other exports (positions, households,
        pending cases, activities) can be in the same pick and are skipped. Any Tax ID
        column is reduced to a one-way code as it is read - the SSN itself is never stored.
      </div>
    </>
  );
}
UploadPanel.propTypes = {
  source: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  onUpload: PropTypes.func.isRequired,
};

// Add a firm, or edit one's name, firm or office. Short code and CRM are fixed
// once saved: the code names the firm's staged rows (and, for Redtail, its
// secrets), and the CRM decides which staging tables those rows live in.
function SourceFormModal({ source, onClose, onSaved }) {
  const editing = Boolean(source);
  const { data: options, isLoading: loadingOptions } = useGetCrmSyncOptionsQuery();
  const [save, { isLoading: saving }] = useSaveCrmSourceMutation();
  const [form, setForm] = useState(() => ({
    label: source?.label || '',
    display_name: source?.display_name || '',
    source_system: source?.source_system || 'redtail',
    firm_id: source?.firm_id ? String(source.firm_id) : '',
    office_fsc_id: source?.office_fsc_id || '',
  }));
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const labelOk = /^[a-z][a-z0-9_]{1,31}$/.test(form.label);
  const canSave = labelOk && form.display_name.trim().length >= 2 && !saving;
  const offices = options?.offices || [];
  const firms = options?.firms || [];

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await save({
        label: form.label,
        display_name: form.display_name.trim(),
        source_system: form.source_system,
        firm_id: form.firm_id ? Number(form.firm_id) : null,
        office_fsc_id: form.office_fsc_id || null,
      }).unwrap();
      onSaved(res.created ? `${form.display_name.trim()} added.` : `${form.display_name.trim()} saved.`);
    } catch (err) {
      setError(err?.data?.detail?.[0]?.msg || err?.data?.detail || 'Could not save the firm.');
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>{editing ? `Edit ${source.display_name}` : 'Add firm'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{typeof error === 'string' ? error : 'Could not save the firm.'}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>CRM</Form.Label>
            <Form.Select value={form.source_system} onChange={set('source_system')} disabled={editing}>
              <option value="redtail">Redtail - read from the Redtail API</option>
              <option value="smartoffice">SmartOffice - refreshed by uploading list exports</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Short code</Form.Label>
            <Form.Control
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
              placeholder="e.g. hammer"
              disabled={editing}
              isInvalid={form.label !== '' && !labelOk}
            />
            <Form.Text muted>
              Lowercase letters, numbers and _ - can&apos;t be changed later.
              {form.source_system === 'redtail' && form.label && (
                <> The Redtail credentials must be set as <code>REDTAIL_{form.label.toUpperCase()}_USER_KEY</code> (or
                {' '}<code>_USERNAME</code> + <code>_PASSWORD</code>) on the api and worker.</>
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Display name</Form.Label>
            <Form.Control value={form.display_name} onChange={set('display_name')} placeholder="e.g. Hammer Financial Group" />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>FSC office</Form.Label>
            <Form.Select value={form.office_fsc_id} onChange={set('office_fsc_id')} disabled={loadingOptions}>
              <option value="">None</option>
              {offices.map((o) => <option key={o.fsc_id} value={o.fsc_id}>{o.name}</option>)}
            </Form.Select>
            <Form.Text muted>
              {!loadingOptions && offices.length === 0
                ? <span className="text-warning">No offices yet - run the FSC sync first. </span>
                : null}
              Decides who sees these clients on Work - CRM. With none, only users who can see every office do.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>Firm <span className="text-muted" style={{ fontSize: '0.8rem' }}>(optional)</span></Form.Label>
            <Form.Select value={form.firm_id} onChange={set('firm_id')} disabled={loadingOptions}>
              <option value="">None</option>
              {firms.map((f) => <option key={f.id} value={f.id}>{f.name}{f.kind === 'b2b' ? ' (B2B)' : ''}</option>)}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!canSave}>
            {saving ? <Spinner animation="border" size="sm" /> : null} {editing ? 'Save' : 'Add firm'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
SourceFormModal.propTypes = {
  source: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

function SourceCard({ source, last, busy, onRun, onUpload, onEdit }) {
  const isFile = source.source_system === 'smartoffice';
  const active = source.active;
  return (
    <Card className={`mb-3 ${active ? 'border-info' : ''}`}>
      <Card.Body>
        <div className="d-flex align-items-baseline gap-2 flex-wrap mb-3">
          <h5 className="mb-0">{source.display_name}</h5>
          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
            {isFile ? 'SmartOffice exports' : 'Redtail database'} <code>{source.label}</code>
            {' · '}{source.office_name ? <>Office: {source.office_name}</> : <span className="text-warning">No FSC office</span>}
          </span>
          <Button variant="link" size="sm" className="p-0 ms-auto" onClick={() => onEdit(source)}>
            <i className="bi bi-pencil" /> Edit
          </Button>
        </div>

        {!isFile && source.credentials_ok === false && (
          <Alert variant="warning" className="py-2" style={{ fontSize: '0.82rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-1" />
            Redtail credentials not found for <code>{source.label}</code>. Set <code>REDTAIL_API_KEY</code> and
            {' '}<code>REDTAIL_{source.label.toUpperCase()}_USER_KEY</code> (or <code>_USERNAME</code> + <code>_PASSWORD</code>)
            {' '}on the api and worker, or a refresh will fail.
          </Alert>
        )}

        <Row className="g-3 mb-3">
          <Col xs={6} md={3}><Stat label={isFile ? 'Contacts uploaded' : 'Contacts in Redtail copy'} value={n(source.contacts)}
            note={source.contacts_deleted > 0 ? `${n(source.contacts_deleted)} deleted in Redtail` : null} /></Col>
          <Col xs={6} md={3}><Stat label={isFile ? 'Policies uploaded' : 'Accounts'} value={n(source.accounts)} /></Col>
          <Col xs={6} md={3}><Stat label="People on the CRM page" value={n(source.people)} note={`${n(source.clients)} clients`} /></Col>
          <Col xs={6} md={3}><Stat label={isFile ? 'Last upload' : 'Last pulled'} value={<span style={{ fontSize: '0.9rem' }}>{when(source.last_pulled_at)}</span>}
            note={`CRM rebuilt ${when(source.last_built_at)}`} /></Col>
        </Row>

        {active ? (
          <Alert variant="info" className="d-flex align-items-center gap-2 mb-0">
            <Spinner animation="border" size="sm" />
            <div>
              <strong>{active.mode === 'upload' ? 'Rebuilding' : active.mode === 'quick' ? 'Quick refresh' : 'Full refresh'}</strong>
              {' - '}{active.progress || 'starting...'}
              <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                Started by {active.started_by} at {when(active.started)}. You can leave this page; it keeps running.
              </div>
            </div>
          </Alert>
        ) : isFile ? (
          <UploadPanel source={source} busy={busy} onUpload={onUpload} />
        ) : (
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <Button variant="primary" onClick={() => onRun(source.label, 'full')} disabled={busy}>
              {busy ? <Spinner animation="border" size="sm" /> : <i className="bi bi-arrow-repeat" />}{' '}
              Full refresh
            </Button>
            <Button variant="outline-primary" onClick={() => onRun(source.label, 'quick')} disabled={busy || !source.last_pulled_at}>
              <i className="bi bi-lightning-charge" /> Quick refresh
            </Button>
          </div>
        )}
        {!active && !isFile && (
          <div className="text-muted mt-2" style={{ fontSize: '0.78rem', maxWidth: '48rem' }}>
            <strong>Full</strong> re-reads every contact and every account, and drops anything deleted in
            Redtail. About {source.contacts > 10000 ? '45 minutes' : '5 minutes'} for this firm.{' '}
            <strong>Quick</strong> reads only contacts changed since the last pull, and their accounts -
            minutes, but it misses an account edited without touching its contact.
          </div>
        )}

        {last && !active && (
          <div className="mt-3 pt-2 border-top" style={{ fontSize: '0.8rem' }}>
            <Badge bg={(STATUS[last.status] || {}).bg || 'secondary'} className="me-2">
              {(STATUS[last.status] || {}).label || last.status}
            </Badge>
            <span className="text-muted">
              Last {last.mode === 'upload' ? 'upload' : `${last.mode} refresh`} {when(last.finished)} by {last.started_by}
              {duration(last) ? ` (${duration(last)})` : ''}
            </span>
            <div className="mt-1">{describe(last)}</div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
SourceCard.propTypes = {
  source: PropTypes.object.isRequired,
  last: PropTypes.object,
  busy: PropTypes.bool,
  onRun: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default function CrmSync() {
  const { data, isLoading, refetch } = useGetCrmSyncQuery();
  const [run, { isLoading: isTriggering }] = useRunCrmSyncMutation();
  const [runMatch, { isLoading: isMatching }] = useRunCrmMatchMutation();
  const [uploadExport, { isLoading: isUploading }] = useUploadCrmExportMutation();
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  // undefined = closed, null = adding, a source = editing it.
  const [editing, setEditing] = useState(undefined);

  const sources = data?.sources || [];
  const runs = data?.runs || [];
  const match = data?.match;
  const anyActive = sources.some((s) => s.active) || !!match?.active;

  // Poll only while something is in flight, so a page left open overnight
  // does not spend it hitting the API.
  useEffect(() => {
    if (!anyActive) return undefined;
    const t = setInterval(refetch, 4000);
    return () => clearInterval(t);
  }, [anyActive, refetch]);

  const go = useCallback(async (source, mode) => {
    setError('');
    setNote('');
    try {
      await run({ source, mode }).unwrap();
      setNote(`${mode === 'quick' ? 'Quick' : 'Full'} refresh started.`);
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not start the refresh.');
    }
  }, [run, refetch]);

  const goUpload = useCallback(async (source, files, update = false) => {
    setError('');
    setNote('');
    try {
      const res = await uploadExport({ source, files, update }).unwrap();
      const imported = res.imported || [];
      const what = imported.filter((f) => f.kind).map((f) => (f.updated !== undefined
        ? `${n(f.rows)} ${f.kind.replace('_', ' ')} (${n(f.updated)} updated, ${n(f.added)} added, ${n(f.on_file)} now on file)`
        : `${n(f.rows)} ${f.kind.replace('_', ' ')}`)).join(', ');
      const skipped = [...new Set(imported.filter((f) => !f.kind).map((f) => f.skipped))];
      setNote(`Uploaded ${what}.${skipped.length ? ` Skipped (not used by the CRM): ${skipped.join(', ')}.` : ''} Rebuilding the CRM records now.`);
      await refetch();
      return true;
    } catch (e) {
      // The card shows this message itself (see UploadPanel).
      if (e?.status === 413) {
        return 'The upload was too large for the server (over 200 MB together, or a file over 60 MB). '
          + 'Upload fewer files at a time, or re-save UTF-16 exports as UTF-8.';
      }
      return (typeof e?.data?.detail === 'string' && e.data.detail) || 'Could not upload the export.';
    }
  }, [uploadExport, refetch]);

  const goMatch = useCallback(async () => {
    setError('');
    setNote('');
    try {
      await runMatch().unwrap();
      setNote('Matching to Salesforce started.');
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not start the match.');
    }
  }, [runMatch, refetch]);

  if (isLoading) {
    return <Container className="py-4 text-center"><Spinner animation="border" /></Container>;
  }

  return (
    <Container className="py-4">
      <h3 className="mb-1">CRM Sync</h3>
      <p className="text-muted" style={{ maxWidth: '48rem' }}>
        Keeps <strong>Work - CRM</strong> current in two steps: refresh each firm from its CRM,
        then match the clients to Salesforce. Neither step changes anything in Redtail,
        SmartOffice or Salesforce.
      </p>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {note && <Alert variant="success" dismissible onClose={() => setNote('')}>{note}</Alert>}

      {editing !== undefined && (
        <SourceFormModal
          source={editing}
          onClose={() => setEditing(undefined)}
          onSaved={async (msg) => { setEditing(undefined); setError(''); setNote(msg); await refetch(); }}
        />
      )}

      <div className="d-flex align-items-center gap-2 mb-2 mt-3">
        <h5 className="mb-0">1. Refresh each firm</h5>
        <Button variant="outline-primary" size="sm" className="ms-auto" onClick={() => setEditing(null)}>
          <i className="bi bi-plus-lg" /> Add firm
        </Button>
      </div>
      {sources.length === 0 && (
        <Alert variant="secondary">
          No firms are set up yet. Use <strong>Add firm</strong> to add each merging firm&apos;s CRM.
        </Alert>
      )}
      <p className="text-muted" style={{ fontSize: '0.82rem', maxWidth: '48rem' }}>
        Redtail firms are read straight from Redtail; SmartOffice firms are refreshed by uploading
        their list exports. Either way the firm&apos;s CRM records are rebuilt, including duplicate
        grouping.
      </p>
      {sources.map((s) => (
        <SourceCard
          key={s.label}
          source={s}
          last={runs.find((r) => r.source_label === s.label && r.finished)}
          busy={isTriggering || isUploading}
          onRun={go}
          onUpload={goUpload}
          onEdit={setEditing}
        />
      ))}

      <h5 className="mb-2 mt-4">2. Match to Salesforce</h5>
      <MatchCard
        match={match}
        sources={sources}
        last={runs.find((r) => r.source_label === MATCH_LABEL && r.finished)}
        busy={isMatching}
        onRun={goMatch}
      />

      <h5 className="mb-2 mt-4">History</h5>
      <Card className="mb-3">
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th style={{ width: '10%' }}>Status</th>
              <th style={{ width: '18%' }}>What</th>
              <th style={{ width: '8%' }}>Mode</th>
              <th style={{ width: '18%' }}>Started</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
            {runs.map((r) => {
              const st = STATUS[r.status] || { bg: 'secondary', label: r.status };
              const src = sources.find((s) => s.label === r.source_label);
              return (
                <tr key={r.id}>
                  <td><Badge bg={st.bg}>{st.label}</Badge></td>
                  <td style={{ fontSize: '0.82rem' }}>{r.source_label === MATCH_LABEL ? 'All firms - Salesforce' : (src?.display_name || r.source_label)}</td>
                  <td style={{ fontSize: '0.82rem', textTransform: 'capitalize' }}>{r.mode}</td>
                  <td style={{ fontSize: '0.82rem' }}>
                    {when(r.started)}
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                      {r.started_by}{duration(r) ? ` · ${duration(r)}` : ''}
                    </div>
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {r.finished ? describe(r) : <span className="text-muted">{r.progress}</span>}
                  </td>
                </tr>
              );
            })}
            {runs.length === 0 && (
              <tr><td colSpan={5} className="text-center text-muted py-4">
                Nothing has been refreshed from this page yet.
              </td></tr>
            )}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
}
